import { useMutation } from "@tanstack/react-query";
import {
    postVerifyReferrer,
    postVerifyEmail,
    postVerifyUser,
    postVerifyEmailCode
} from "./api";
import { useStytch } from "@stytch/react";
import type { IStartUserEmailAuthSessionParams, IVerifyEmailBody, IVerifyEmailCodeBody, IVerifyReferrerBody, IVerifyUserBody, IVerifyUserResponse, IStartUserOAuthSessionParams, ISessionResponse } from "./types";

const useVerifyReferrer = () => {
  return useMutation({
    mutationFn: (body: IVerifyReferrerBody) => postVerifyReferrer(body),
  });
};

const useSendEmailVerificationCode = () => {
  return useMutation({
    mutationFn: (body: IVerifyEmailBody) => {
      return postVerifyEmail(body);
    },
  });
};

const useVerifyUser = () => {   
  return useMutation({
    mutationFn: (body: IVerifyUserBody) => postVerifyUser(body),
  });
};

const useVerifyEmailCodeMutation = () => {
  return useMutation({
    mutationFn: (body: IVerifyEmailCodeBody) => postVerifyEmailCode(body),
  });
};

const useStytchOAuthSession = () => {
  const stytch = useStytch();

  return useMutation({
    mutationFn: (token: string) => stytch.oauth.authenticate(token, {
      session_duration_minutes: 60,
    }),
  });
};


const useStartUserEmailAuthSession = () => {
  const verifyEmailCode = useVerifyEmailCodeMutation();
  const verifyUser = useVerifyUser();

  return useMutation({
    mutationFn: async (params: IStartUserEmailAuthSessionParams): Promise<IVerifyUserResponse> => {
      const { code, email, username, referrer } = params;

      // Step 1: Verify the email code
      const sessionData = await verifyEmailCode.mutateAsync({
        code,
        email,
        username,
      }) as ISessionResponse;

      if (!sessionData?.sessionDetails) {
        throw new Error("Invalid session data received");
      }

      // Step 2: Start the user session with the verified data
      const userData = await verifyUser.mutateAsync({
        provider: "stytch",
        userId: sessionData.sessionDetails.user_id,
        firstName: sessionData.sessionDetails.user.name.first_name,
        lastName: sessionData.sessionDetails.user.name.last_name,
        email: sessionData.sessionDetails.user.emails?.[0]?.email ?? "",
        username: sessionData.username,
        referrer,
      });

      return userData;
    },
    onSuccess: (data, variables) => {
      let redirectUrl = '';

      if (variables.referrer?.indexOf('?') != -1 || variables.referrer?.indexOf('&') != -1) {
        redirectUrl = variables.referrer + `&token=${data.token}&username=${data.username}`;
      } else {
        redirectUrl = variables.referrer + `?token=${data.token}&username=${data.username}`;
      }

      window.location.replace(redirectUrl);
    },
  });
};

const useStartUserOAuthSession = () => {
  const stytchOAuthSession = useStytchOAuthSession();
  const verifyUser = useVerifyUser();

  return useMutation({
    mutationFn: async (params: IStartUserOAuthSessionParams): Promise<IVerifyUserResponse> => {
      const { token, referrer, username } = params;

      // Step 1: Verify the email code
      const sessionData = await stytchOAuthSession.mutateAsync(token);

      if (!sessionData) {
        throw new Error("Invalid session data received");
      }

      // Step 2: Start the user session with the verified data
      const userData = await verifyUser.mutateAsync({
        provider: sessionData.provider_type,
        userId: sessionData.provider_subject,
        firstName: sessionData.user.name.first_name,
        lastName: sessionData.user.name.last_name,
        email: sessionData.user.emails?.[0]?.email ?? "",
        username,
        referrer,
      });

      return userData;
    },
  });
};

export { 
  useVerifyReferrer, 
  useSendEmailVerificationCode, 
  useVerifyUser, 
  useVerifyEmailCodeMutation,
  useStartUserEmailAuthSession,
  useStartUserOAuthSession
};